"use client"

/**
 * セッション締め出しオーバーレイ（frontend/shared/auth）。
 *
 * 同一アプリを別端末で後からログインした場合に、古い端末を全画面で覆って操作を止める。
 * アプリのUIキットに依存しないよう inline style で自己完結させる（cast/driver/staff 共通利用）。
 * 「ログインし直す」で onRelogin（各アプリの signOut→ログイン遷移）を呼ぶ。
 */

import type { CSSProperties } from "react"

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 2147483647, // 最前面（アプリ内のどのモーダルより上）
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "20px",
  padding: "24px",
  textAlign: "center",
  backgroundColor: "rgba(17, 24, 39, 0.92)",
  color: "#fff",
  backdropFilter: "blur(2px)",
}

const buttonStyle: CSSProperties = {
  padding: "12px 28px",
  fontSize: "16px",
  fontWeight: 700,
  color: "#1d4ed8",
  backgroundColor: "#fff",
  border: "none",
  borderRadius: "9999px",
  cursor: "pointer",
}

export interface SessionLockOverlayProps {
  /** 「ログインし直す」押下時の処理（通常は signOut → /login 遷移） */
  onRelogin: () => void
  /** 見出し。既定「別の端末でログインされました」 */
  title?: string
  /** 説明文。既定は下記 */
  description?: string
}

export function SessionLockOverlay({
  onRelogin,
  title = "別の端末でログインされました",
  description = "この端末での操作は停止されました。続けて使う場合はログインし直してください。",
}: SessionLockOverlayProps) {
  return (
    <div style={overlayStyle} role="alertdialog" aria-modal="true" aria-label={title} data-testid="session-lock-overlay">
      <div style={{ fontSize: "20px", fontWeight: 700 }}>{title}</div>
      <div style={{ fontSize: "15px", maxWidth: "420px", lineHeight: 1.6, opacity: 0.9 }}>{description}</div>
      <button type="button" style={buttonStyle} onClick={onRelogin} data-testid="session-lock-relogin">
        ログインし直す
      </button>
    </div>
  )
}
